import type { MailLinkDto } from "../../../services/mailLinkService";
import type { DocumentDto } from "../../../services/documentService";

/**
 * Was sich auf eine To-do-Zeile ziehen lässt (PO 31.08.2026: „mails, doks
 * sollte man per drag and drop in ein todo packen können").
 *
 * Reine Ableitungen — DOM-frei, damit der node-only Vitest-Runner sie prüft.
 * Die Handhabung des `DataTransfer` bleibt in der Komponente.
 *
 * Eigene MIME-Typen statt `text/plain`: nur so lässt sich beim Überfahren
 * ENTSCHEIDEN, ob die Zeile den Zug annehmen darf. Ein Drop, der erst beim
 * Loslassen scheitert, ist schlechter als einer, der gar nicht erst
 * hervorgehoben wird. Der Browser liefert die Nutzlast selbst erst beim Drop
 * (`getData` ist während `dragover` leer) — die TYPEN aber immer, und mehr
 * braucht die Entscheidung nicht.
 */

export const ZIEHTYP_MAIL = "application/x-kmvs-mail";
export const ZIEHTYP_DOKUMENT = "application/x-kmvs-dokument";
/**
 * Mail bzw. Anhänge aus dem POSTFACH (Mailliste, Mailfenster) — Nutzlast und
 * Leser in `mail/postfachZug.ts`. Hier nur die Typen, damit {@link dropArt}
 * sie kennt, ohne das Mail-Modul zu laden.
 */
export const ZIEHTYP_POSTFACH_MAIL = "application/x-kmvs-postfach-mail";
export const ZIEHTYP_MAIL_ANHANG = "application/x-kmvs-mail-anhang";
/**
 * Mail aus dem NEUEN Outlook für Windows — es liefert keine Datei, nur diesen
 * String-Typ mit EWS-Ids (Leser und Übersetzung in `mail/neuesOutlook.ts`).
 */
export const ZIEHTYP_NEUES_OUTLOOK = "maillistrow";

/**
 * Welchen Zugtyp das neue Outlook gerade liefert. Je nach Ansicht heißt er
 * anders — gemessen: `maillistrow` (Mails) und `multimaillistmessagerows`
 * (Nachrichtenliste, User 03.11.2026). Deshalb jeder Typ mit „maillist“.
 */
export function neuesOutlookTyp(typen: readonly string[] | undefined): string | null {
    return (typen ?? []).find((t) => /maillist/i.test(t)) ?? null;
}

/** Nutzlast einer gezogenen Mail — der Snapshot, aus dem der MailLink entsteht. */
export interface MailZug {
    /**
     * Id der Verknüpfung (`MailLink`) — damit hängt das Verfassen-Fenster die
     * Mail als `.eml` an (User 22.10.2026). Der Server prüft über sie dieselbe
     * Leseregel wie beim Öffnen im Vorgang.
     */
    linkId?: number | null;
    mailbox: string;
    messageId: string;
    conversationId?: string | null;
    /**
     * RFC-Message-Id — reist mit, damit der daraus entstehende MailLink sie
     * behält. Ohne sie fiele eine an ein To-do gezogene Mail beim Entdoppeln
     * auf die Graph-`id` zurück, und die zweite Ordnerkopie derselben Mail
     * käme erneut durch.
     */
    internetMessageId?: string | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    recipients?: string | null;
    receivedAt?: string | null;
    preview?: string | null;
}

/** Nutzlast eines gezogenen Dokuments. */
export interface DokumentZug {
    id: number;
    fileName: string;
    /**
     * T-225: Typ und Größe für das Verfassen-Fenster (Anhang-Chip,
     * Größenanzeige). Optional — der Server bestimmt beides beim Senden
     * ohnehin selbst aus der Datenbank.
     */
    mimeType?: string | null;
    fileSize?: number | null;
}

/** Was eine To-do-Zeile aus einem Zug machen kann. */
export type DropArt = "mail" | "dokument" | "postfachMail" | "anhang" | "neuesOutlook" | "dateien" | null;

/**
 * Welche Art steckt in diesem Zug? Reihenfolge ist Absicht: liegt eine
 * KMVS-Nutzlast an, gewinnt sie — Firefox meldet bei einem internen Zug
 * zusätzlich `Files`, und ohne diese Reihenfolge landete eine gezogene Mail
 * im Datei-Upload.
 */
export function dropArt(typen: readonly string[] | undefined): DropArt {
    const liste = typen ?? [];
    if (liste.includes(ZIEHTYP_MAIL)) return "mail";
    if (liste.includes(ZIEHTYP_DOKUMENT)) return "dokument";
    if (liste.includes(ZIEHTYP_POSTFACH_MAIL)) return "postfachMail";
    if (liste.includes(ZIEHTYP_MAIL_ANHANG)) return "anhang";
    if (liste.includes("Files")) return "dateien";
    // Nach `Files`: Liefert ein Programm beides, bleibt es beim Datei-Weg.
    if (neuesOutlookTyp(liste)) return "neuesOutlook";
    return null;
}

/**
 * Dateiname einer als Anhang gezogenen Mail — „<Betreff>.eml" wie in Outlook.
 * Nur Anzeige: den echten Namen setzt der Server beim Senden (dieselbe Regel).
 */
export function mailAnhangName(subject: string | null | undefined): string {
    const roh = String(subject ?? "")
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u001f\u007f]/g, " ")
        .replace(/[/\\:*?"<>|]/g, "-")
        .replace(/\s+/g, " ")
        .trim();
    return `${(roh || "Ohne Betreff").slice(0, 150).trimEnd()}.eml`;
}

/** Beschriftung der hervorgehobenen Zeile — sagt, was gleich passiert. */
export function dropHinweis(art: DropArt, todoTitel: string): string {
    if (art === "mail") return `Mail an \u201e${todoTitel}\u201c hängen`;
    if (art === "dokument") return `Dokument an \u201e${todoTitel}\u201c hängen`;
    if (art === "postfachMail") return `Mail an \u201e${todoTitel}\u201c hängen`;
    if (art === "anhang") return `Anhang an \u201e${todoTitel}\u201c ablegen`;
    if (art === "neuesOutlook") return `Mail aus Outlook an \u201e${todoTitel}\u201c hängen`;
    if (art === "dateien") return `Dateien an \u201e${todoTitel}\u201c hochladen`;
    return "";
}

/** Den Snapshot einer verknüpften Mail in eine Zug-Nutzlast übersetzen. */
export function mailZugAusLink(link: MailLinkDto): MailZug {
    return {
        linkId: link.id,
        mailbox: link.mailbox,
        messageId: link.messageId,
        conversationId: link.conversationId ?? null,
        internetMessageId: link.internetMessageId ?? null,
        subject: link.subject ?? null,
        senderName: link.senderName ?? null,
        senderEmail: link.senderEmail ?? null,
        recipients: link.recipients ?? null,
        receivedAt: link.receivedAt ?? null,
        preview: link.preview ?? null,
    };
}

export function dokumentZugAus(dokument: DocumentDto): DokumentZug {
    return {
        id: dokument.id,
        fileName: dokument.fileName,
        mimeType: dokument.mimeType ?? null,
        fileSize: dokument.fileSize ?? null,
    };
}

/**
 * Nutzlast aus dem Zug lesen.
 *
 * Fällt defensiv auf `null` zurück: der Inhalt eines `DataTransfer` kommt aus
 * dem Browser und kann von einer anderen Seite stammen, die zufällig denselben
 * MIME-Typ benutzt. Ein Absturz beim Loslassen wäre die schlechteste Antwort
 * darauf.
 */
export function leseMailZug(roh: string | null | undefined): MailZug | null {
    const daten = parse(roh);
    if (!daten) return null;
    const mailbox = typeof daten.mailbox === "string" ? daten.mailbox.trim() : "";
    const messageId = typeof daten.messageId === "string" ? daten.messageId.trim() : "";
    if (!mailbox || !messageId) return null;
    const text = (wert: unknown): string | null => (typeof wert === "string" && wert ? wert : null);
    const linkId = Number(daten.linkId);
    return {
        linkId: Number.isInteger(linkId) && linkId > 0 ? linkId : null,
        mailbox,
        messageId,
        conversationId: text(daten.conversationId),
        internetMessageId: text(daten.internetMessageId),
        subject: text(daten.subject),
        senderName: text(daten.senderName),
        senderEmail: text(daten.senderEmail),
        recipients: text(daten.recipients),
        receivedAt: text(daten.receivedAt),
        preview: text(daten.preview),
    };
}

export function leseDokumentZug(roh: string | null | undefined): DokumentZug | null {
    const daten = parse(roh);
    if (!daten) return null;
    const id = Number(daten.id);
    if (!Number.isInteger(id) || id <= 0) return null;
    const groesse = typeof daten.fileSize === "number" ? daten.fileSize : NaN;
    return {
        id,
        fileName: typeof daten.fileName === "string" ? daten.fileName : `Dokument ${id}`,
        mimeType: typeof daten.mimeType === "string" && daten.mimeType ? daten.mimeType : null,
        fileSize: Number.isFinite(groesse) && groesse >= 0 ? groesse : null,
    };
}

/**
 * Mehrere Dokumente auf einmal (User 22.10.2026: im Reiter „Dokumente"
 * markierte Dateien ins Mailfenster ziehen).
 *
 * Genau EIN Dokument ohne Ordner reist im bisherigen Einzelformat — so
 * verstehen es auch die To-do-Zeilen des Vorgangs, die nur
 * {@link leseDokumentZug} kennen. Alles andere wird ein Umschlag
 * `{ dokumente, ordner }`: `ordner` zählt mitmarkierte Ordner, die sich nicht
 * anhängen lassen — damit das Ziel sie nicht stillschweigend unterschlägt,
 * sondern es sagen kann. Ein Einzel-Leser sieht im Umschlag keine `id` und
 * weist ihn ab, statt Falsches anzuhängen.
 */
export interface DokumentZuege {
    dokumente: DokumentZug[];
    /** Mitgezogene Ordner — werden nicht angehängt, nur gemeldet. */
    ordner: number;
}

export function dokumentZuegeNutzlast(dokumente: readonly DocumentDto[], ordner = 0): string {
    const zuege = dokumente.map(dokumentZugAus);
    if (zuege.length === 1 && ordner === 0) return JSON.stringify(zuege[0]);
    return JSON.stringify({ dokumente: zuege, ordner });
}

/** Liest Einzel- wie Mehrfach-Nutzlast; Unbrauchbares fällt still heraus. */
export function leseDokumentZuege(roh: string | null | undefined): DokumentZuege {
    const daten = parse(roh);
    if (!daten) return { dokumente: [], ordner: 0 };
    if (!Array.isArray(daten.dokumente)) {
        const einzeln = leseDokumentZug(roh);
        return { dokumente: einzeln ? [einzeln] : [], ordner: 0 };
    }
    const dokumente = daten.dokumente
        .map((d) => leseDokumentZug(JSON.stringify(d)))
        .filter((d): d is DokumentZug => d != null);
    const ordner = Number(daten.ordner);
    return { dokumente, ordner: Number.isInteger(ordner) && ordner > 0 ? ordner : 0 };
}

function parse(roh: string | null | undefined): Record<string, unknown> | null {
    if (!roh) return null;
    try {
        const daten = JSON.parse(roh);
        return daten && typeof daten === "object" && !Array.isArray(daten)
            ? (daten as Record<string, unknown>)
            : null;
    } catch {
        return null;
    }
}
